// src/helpers/layout.js
import React, { useEffect, useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLangStore, UI_LOCALES, ENABLED_LOCALES } from "../stores/lang.store.ts";
import { TextButton, ElevatedButton, IconButton } from "../component/common/Buttons";
import { userStore } from "../stores/user.store.ts";
import { authTokenStore } from "../stores/authToken.store.ts";
import { AuthRepository } from "../repositories/auth.repository.ts";
import { useNotificationStore } from "../stores/notification.store.ts";
import { NotificationRepository } from "../repositories/notification.repository.ts";
import { useCurrencyStore, CURRENCIES } from "../stores/currency.store.ts";
import { useServiceStore } from "../stores/service.store.ts";
import { APP_STORE_URL, PLAY_STORE_URL } from "./appLinks";
// IS_DEV: dev/stage 빌드에서만 내부 배달 기능·항공(Flight status)·배달주문 메뉴를 노출한다.
// prod 는 Halal Delivery 를 기존 웹 배달 페이지(/Delivery/)로 연결하고 나머지는 숨긴다.
import { IS_DEV, LOGO_WRAP_SRC } from "./brand";
import { trackMenuClick } from "./clickTracking";

const openAdminPage = () => {
  trackMenuClick("partner", "Partner");
  // stage(dev.korehalal.com)에서는 파트너 콘솔도 stage(dev-partner)로 연결.
  window.open(
    IS_DEV
      ? "https://dev-partner.korehalal.com/"
      : "https://partner.korehalal.com/",
    "_blank",
    "noopener,noreferrer"
  );
};

const openMICEPage = () => {
  trackMenuClick("mice", "MICE");
  window.open("https://icucompany.com/", "_blank", "noopener,noreferrer");
};

const openBlogPage = () => {
  trackMenuClick("blog", "Blog");
  window.open("https://www.korehalal.com/blog/", "_blank", "noopener,noreferrer");
};

// 드로어 메뉴 한 줄 — 라벨(+ 안 읽음 점)과 오른쪽 ›.
function MenuRow({ label, badge, disabled, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`w-full flex items-center justify-between rounded-lg px-4 py-4 text-left hover:bg-bg-interactive-secondaryHovered ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    >
      <span className="flex items-center gap-2 text-text-primary">
        {label}
        {badge && <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />}
      </span>
      <span className="text-text-secondary">›</span>
    </button>
  );
}

function RightDrawer({ open, onClose, user, onGo, onSignOut, signingOut, hasUnread }) {
  const { t } = useTranslation();

  const menuRows = (items) =>
    items.map((item) => (
      <MenuRow
        key={item.slug}
        label={t(item.tKey)}
        badge={item.badge}
        onClick={() => {
          trackMenuClick(item.slug, item.track);
          if (item.href) window.open(item.href, "_blank", "noopener,noreferrer");
          else onGo(item.to);
        }}
      />
    ));

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <div className={`fixed inset-0 z-50 ${open ? "pointer-events-auto" : "pointer-events-none"}`} aria-hidden={!open}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-black/60 transition-opacity duration-200 ${open ? "opacity-100" : "opacity-0"}`}
      />

      {/* Panel */}
      <aside
        className={`absolute end-0 top-0 h-full w-[360px] max-w-[90vw] bg-white shadow-xl
        transition-transform duration-200 ease-out
        ${open ? "translate-x-0" : "translate-x-full rtl:-translate-x-full"}`}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="p-5 border-b">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <img
                src={user?.image || "/illustrations/default_profile_user.png"}
                alt={t("drawer.profile_alt")}
                className="h-12 w-12 rounded-full object-cover border"
              />
              <div>
                <div className="text-base text-body-lg-sb text-text-primary">
                  {user?.user_name || user?.full_name || t("drawer.guest")}
                </div>

                <button
                  type="button"
                  className="text-sm text-body-sm-sb text-text-interactive-secondary hover:underline hover:text-text-interactive-secondaryHovered"
                  onClick={() => onGo(user ? "/my-profile" : "/sign-in")}
                >
                  {user ? t("drawer.my_profile") : t("header.sign_in")}
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="h-9 w-9 rounded-lg inline-flex items-center justify-center"
              aria-label={t("drawer.close")}
            >
              ✕
            </button>
          </div>

          <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
            <button onClick={() => { trackMenuClick("coupon", "My Coupon"); onGo("/coupon"); }} className="block mt-4 w-full text-left">
              <div className="group">
                <div className="rounded-lg bg-bg-secondary p-4 transition-all duration-200 group-hover:ring-1 group-hover:ring-border-interactive-primary group-hover:bg-white">
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary text-body-md transition-colors group-hover:text-text-primary">
                      {t("drawer.my_coupon")}
                    </span>
                    <span className="opacity-0 heading-md transition-all duration-200 group-hover:opacity-100 group-hover:text-text-interactive-primaryHovered group-hover:heading-xl inline-block">
                      &#8250;
                    </span>
                  </div>
                </div>
              </div>
            </button>


            <button onClick={() => { trackMenuClick("bookmark", "My Bookmark"); onGo("/bookmark"); }} className="block mt-4 w-full text-left">
              <div className="group">
                <div className="rounded-lg bg-bg-secondary p-4 transition-all duration-200 group-hover:ring-1 group-hover:ring-border-interactive-primary group-hover:bg-white">
                  <div className="flex items-center justify-between">
                    <span className="text-text-secondary text-body-md transition-colors group-hover:text-text-primary">
                      {t("drawer.my_bookmark")}
                    </span>
                    <span className="opacity-0 heading-md transition-all duration-200 group-hover:opacity-100 group-hover:text-text-interactive-primaryHovered group-hover:heading-xl inline-block">
                      &#8250;
                    </span>
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Menu — 서비스 */}
        <nav className="p-2">
          {menuRows([
            { track: "Halal Map", tKey: "nav.halal_map", to: "/halal-map", slug: "halal_map" },
            IS_DEV
              ? { track: "Halal Delivery", tKey: "nav.halal_delivery", to: "/delivery", slug: "delivery" }
              : { track: "Halal Delivery", tKey: "nav.halal_delivery", href: "/Delivery/", slug: "delivery" },
            { track: "Ummah", tKey: "nav.ummah", to: "/community", slug: "community" },
            ...(IS_DEV ? [{ track: "Flight status", tKey: "nav.flight_status", to: "/airport", slug: "airport" }] : []),
          ])}
        </nav>

        <div className="my-2 border-t" />

        <nav className="p-2">
          {menuRows([
            { track: "Booking", tKey: "nav.booking", to: "/reservation", slug: "booking" },
            ...(IS_DEV ? [{ track: "Delivery Order", tKey: "nav.delivery_order", to: "/delivery/orders", slug: "delivery_orders" }] : []),
            { track: "Review", tKey: "nav.review", to: "/review", slug: "review" },
            { track: "Notification", tKey: "nav.notification", to: "/notification", slug: "notification", badge: hasUnread },
          ])}

          <div className="my-2 border-t" />

          {menuRows([
            { track: "About Us", tKey: "nav.about_us", to: "/about-us", slug: "about_us" },
            { track: "Customize Trip", tKey: "nav.customize_trip", to: "/trip-inquiry", slug: "customize_trip" },
            { track: "Become a Partner", tKey: "nav.become_partner", to: "/partner-inquiry", slug: "become_partner" },
          ])}

          <div className="my-2 border-t" />

          <MenuRow
            label={signingOut ? t("drawer.signing_out") : t("drawer.sign_out")}
            disabled={signingOut}
            onClick={() => { trackMenuClick("sign_out", "Sign-out"); onSignOut(); }}
          />
        </nav>
      </aside>
    </div>
  );
}

function Header() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const accessToken = authTokenStore((s) => s.accessToken);
  const user = userStore((s) => s.user);
  const isLoggedIn = Boolean(accessToken && user);
  const hasUnread = useNotificationStore((s) => s.hasUnread);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const openDrawer = () => {
    setDrawerOpen(true);
    if (isLoggedIn) {
      NotificationRepository.checkNewNotifications();
    }
  };

  const goSignIn = () => navigate("/sign-in");

  const onGo = (to) => {
    setDrawerOpen(false);
    navigate(to);
  };

  const onSignOut = async () => {
    if (signingOut) return;

    setSigningOut(true);
    try {
      await AuthRepository.signOut();
    } catch (e) {
      console.error("Sign-out error:", e);
    } finally {
      authTokenStore.getState().clearTokens();
      userStore.getState().clearUser();

      setDrawerOpen(false);
      navigate("/home", { replace: true });

      setSigningOut(false);
    }
  };

  return (
    <header className="border-b bg-white shadow-sm">
      <div className="mx-auto max-w-[1200px] px-6 py-4">
        <div className="flex flex-col items-center gap-2 sm:flex-row sm:justify-between sm:gap-4">
          <Link to="/home" className="inline-flex items-center">
            <img src={LOGO_WRAP_SRC} alt="Go to main" className="h-10 w-auto" />
          </Link>

          <div className="flex flex-nowrap items-center justify-center gap-1 sm:gap-2 w-full sm:w-auto">
            <TextButton onClick={openBlogPage} className="shrink-0 px-2 sm:px-4">Blog</TextButton>
            <TextButton onClick={openMICEPage} className="shrink-0 px-2 sm:px-4">MICE</TextButton>
            <TextButton onClick={openAdminPage} className="shrink-0 px-2 sm:px-4">{t("header.partner")}</TextButton>

            {!isLoggedIn ? (
              <ElevatedButton onClick={goSignIn} className="shrink-0 px-3 sm:px-4">{t("header.sign_in")}</ElevatedButton>
            ) : (
              <div className="relative shrink-0">
                <IconButton
                  onClick={openDrawer}
                  iconSrc={user?.image || "/illustrations/default_profile_user.png"}
                  iconAlt={t("drawer.profile_alt")}
                  size={40}
                  iconSize={32}
                  rounded="full"
                />
                {hasUnread && (
                  <span className="absolute top-0 right-0 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-white pointer-events-none" />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <RightDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        user={user}
        onGo={onGo}
        onSignOut={onSignOut}
        signingOut={signingOut}
        hasUnread={hasUnread}
      />
    </header>
  );
}

function Footer() {
  const { t } = useTranslation();
  const { lang, setLang } = useLangStore();
  const user = userStore((s) => s.user);
  const { currency, setCurrency } = useCurrencyStore();
  const activeCurrency = user?.currency ?? currency;

  const handleCurrencyChange = async (next) => {
    // 로그인 여부와 무관하게 store 를 먼저 업데이트해야 PriceDisplay 가 즉시 반응
    setCurrency(next);

    const svc = useServiceStore.getState();
    svc.clear();
    svc.clearSearchServices();
    svc.clearServiceDetail();
    svc.clearCategoryServices();

    if (user) {
      const updated = await AuthRepository.updateProfile({ currency: next });
      if (updated) userStore.getState().setUser(updated);
    }
  };

  const socialLinks = [
    { icon: 'instagram', label: 'Instagram', url: 'https://www.instagram.com/korehalal_trip' },
    { icon: 'tiktok', label: 'TikTok', url: 'https://www.tiktok.com/@korehalal_trip' },
    { icon: 'facebook', label: 'Facebook', url: 'https://www.facebook.com/profile.php?id=100093570421999' },
    { icon: 'whats_app', label: 'WhatsApp', url: 'https://wa.me/821026074464' },
  ];

  const storeLinks = [
    { icon: 'appstore', label: 'App Store', url: APP_STORE_URL },
    { icon: 'playstore', label: 'Google Play', url: PLAY_STORE_URL },
  ];

  // Footer 링크로 페이지 전환 시 새 페이지 상단으로 스크롤(SPA 기본은 스크롤 유지).
  const scrollTop = () => window.scrollTo({ top: 0, left: 0 });

  return (
    <footer className="w-full bg-[#F6F5F3] border-t border-[#002113]">
      <div className="mx-auto max-w-[1200px] px-6 py-6 flex flex-col">
        {/* Top row: 여행/서비스 액션 링크 + 결제 통화 */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <nav className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <Link
              to="/trip-inquiry"
              onClick={scrollTop}
              className="inline-flex items-center gap-1 text-body-md-sb text-text-interactive-primary hover:opacity-80"
            >
              <span className="material-symbols-outlined text-[18px] leading-none">flight_takeoff</span>
              {t("nav.customize_trip")}
            </Link>
            <Link
              to="/partner-inquiry"
              onClick={scrollTop}
              className="inline-flex items-center gap-1 text-body-md text-text-secondary hover:text-text-primary"
            >
              <span className="material-symbols-outlined text-[18px] leading-none">handshake</span>
              {t("nav.become_partner")}
            </Link>
            <Link
              to="/guest-booking"
              onClick={scrollTop}
              className="inline-flex items-center gap-1 text-body-md text-text-secondary hover:text-text-primary"
            >
              <span className="material-symbols-outlined text-[18px] leading-none">confirmation_number</span>
              {t("footer.check_nonmember_booking")}
            </Link>
          </nav>
          <div className="flex items-center gap-2">
            {/* 언어 선택기: 활성 로케일(ENABLED_LOCALES)이 2개 이상일 때만 노출 */}
            {UI_LOCALES.filter((l) => ENABLED_LOCALES.includes(l.code)).length > 1 && (
              <select
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                aria-label={t("footer.language")}
                className="text-body-md text-text-secondary bg-transparent border border-[#ACABA9] rounded px-2 py-1 cursor-pointer hover:border-[#002113] focus:outline-none"
              >
                {UI_LOCALES.filter((l) => ENABLED_LOCALES.includes(l.code)).map((l) => (
                  <option key={l.code} value={l.code}>{l.label}</option>
                ))}
              </select>
            )}
            <select
              value={activeCurrency}
              onChange={(e) => handleCurrencyChange(e.target.value)}
              className="text-body-md text-text-secondary bg-transparent border border-[#ACABA9] rounded px-2 py-1 cursor-pointer hover:border-[#002113] focus:outline-none"
            >
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="my-6 border-t border-[#ACABA9]" />

        {/* Social + Store row */}
        <div className="flex flex-wrap justify-between gap-6">
          <div className="flex gap-6">
            {socialLinks.map(link => (
              <a key={link.icon} href={link.url} target="_blank" rel="noopener noreferrer">
                <img src={`/icons/${link.icon}.png`} alt={link.label} width={40} height={40} loading="lazy" className="w-10 h-10" />
              </a>
            ))}
          </div>
          <div className="flex gap-3">
            {storeLinks.map(link => (
              <a key={link.icon} href={link.url} target="_blank" rel="noopener noreferrer">
                <img src={`/icons/${link.icon}.png`} alt={link.label} height={40} loading="lazy" className="h-10 w-auto" />
              </a>
            ))}
          </div>
        </div>

        <div className="my-6 border-t border-[#ACABA9]" />

        {/* Company info */}
        <div className="flex flex-col gap-1">
          <img src={LOGO_WRAP_SRC} alt="Logo" className="h-[18px] object-contain self-start mb-4" />
          <p className="m-0 text-body-md text-text-secondary">Korehalal Trip</p>
          <p className="m-0 text-body-md text-text-secondary">cs@icucompany.com</p>
          <p className="m-0 text-body-md text-text-secondary">#205, 211, Hwarang-ro, Seongbuk-gu, Seoul, Korea</p>
          <p className="m-0 text-body-md text-text-secondary md:hidden">sales No. 2024-Gyeonggi Hanam-0177</p>
          <p className="m-0 text-body-md text-text-secondary md:hidden">Business No. 540-81-03858</p>
          <p className="m-0 text-body-md text-text-secondary hidden md:block">Sales No. 2024-Gyeonggi Hanam-0177&nbsp;&nbsp;Business No. 540-81-03858</p>
        </div>

        <div className="my-6 border-t border-[#ACABA9]" />

        {/* Bottom row: 회사 소개 / 약관 링크 */}
        <nav className="flex flex-wrap gap-x-5 gap-y-2">
          <Link to="/about-us" onClick={scrollTop} className="text-body-md text-text-secondary hover:text-text-primary">
            {t("nav.about_us")}
          </Link>
          <Link to="/terms-of-service" onClick={scrollTop} className="text-body-md text-text-secondary hover:text-text-primary">
            {t("footer.terms")}
          </Link>
          <Link to="/privacy-policy" onClick={scrollTop} className="text-body-md text-text-secondary hover:text-text-primary">
            {t("footer.privacy")}
          </Link>
          <Link to="/refund-policy" onClick={scrollTop} className="text-body-md text-text-secondary hover:text-text-primary">
            {t("footer.refund")}
          </Link>
        </nav>

        <div className="my-6 border-t border-[#ACABA9]" />

        <p className="m-0 text-body-md text-text-tertiary">
          {t("footer.copyright")}
        </p>
        <div className="h-2" />
      </div>
    </footer>
  );
}

export default function Layout() {
  // 페이지뷰 추적은 Router.js 의 AppRoutes 로 옮겼다 — 랜딩("/")이 Layout
  // 밖에 있어서 여기 두면 첫 화면이 통째로 집계에서 빠진다.
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1">
        <div className="mx-auto max-w-[1200px] px-6 py-6">
          <Outlet />
        </div>
      </main>
      <Footer />
    </div>
  );
}
